import { useEffect, useState } from "react";
import {
  createMonthlyProcess,
  deleteMonthlyProcess,
  getProcessOwners
} from "../services/monthlyProcessService";

export default function ProjectDetailModal({
  editProject,
  setEditProject,
  setSelectedProject,
  canEdit,
  detailInputStyle,
  updateProject,
  deleteProject,
  uploadProjectFile,
  deleteProjectFile,
  fileCategory,
  setFileCategory,
  calculateKalanAySureForProject,
  calculateProjeSuresiForProject,
  dateInputValue,
  setPreviewFile,
  isUploadingFile,
  uploadProgress,
  uploadingFileName,
  uploadTotalCount,
  uploadedCount,
  isDraggingFile,
  setIsDraggingFile,
  processProjects = [],
  companyOptions = [],
  currentUserEmail = "",
  isAdmin = false
}) {

  const isDark = document.body.getAttribute("data-theme") === "dark";
  const [fileSearchText, setFileSearchText] = useState("");
  const [fileFilterCategory, setFileFilterCategory] = useState("all");
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [projectNoteText, setProjectNoteText] = useState("");
  const [taskForm, setTaskForm] = useState({
    noteIndex: null,
    processProjectId: "",
    owner: ""
  });
  const [processOwners, setProcessOwners] = useState([]);
  const normalizeName = (value) => String(value || "").trim().toLocaleLowerCase("tr-TR").replace(/\s+/g, " ");
  const selectedCompany = companyOptions.find((company) => company.id === editProject?.companyId)
    || companyOptions.find((company) => (
      normalizeName(company.firmaAdi) === normalizeName(editProject?.firmaAdi)
      || normalizeName(company.firmaKisaAdi) === normalizeName(editProject?.firmaKisaAdi)
    ));
  const branchOptions = selectedCompany?.branches || [];
  const selectedBranch = branchOptions.find((branch) => branch.id === editProject?.branchId)
    || branchOptions.find((branch) => normalizeName(branch.technoparkName) === normalizeName(editProject?.teknoparkAdi));

  const changeProjectBranch = (branchId) => {
    const branch = branchOptions.find((item) => item.id === branchId);
    setEditProject({
      ...editProject,
      companyId: selectedCompany?.id || editProject?.companyId || "",
      branchId,
      teknoparkAdi: branch?.technoparkName || editProject?.teknoparkAdi || ""
    });
  };

  useEffect(() => {
    if (!taskForm.processProjectId) return;

    getProcessOwners(taskForm.processProjectId).then(setProcessOwners).catch(console.error);
  }, [taskForm.processProjectId]);

  const projectNotes = Array.isArray(editProject?.notes) ? editProject.notes : [];

  const addProjectNote = async () => {
    const safeText = projectNoteText.trim();
    if (!safeText) return;

    const nextNotes = [
      ...projectNotes,
      {
        text: safeText,
        createdAt: new Date().toISOString(),
        createdBy: currentUserEmail || ""
      }
    ];

    setEditProject({ ...editProject, notes: nextNotes });
    setProjectNoteText("");
    await updateProject({ ...editProject, notes: nextNotes }, { silent: true });
  };

  const createTaskFromNote = async (noteItem, noteIndex) => {
    if (noteItem.linkedTaskId) {
      alert("Bu nottan daha önce görev kartı oluşturulmuş.");
      return;
    }

    if (!taskForm.processProjectId || !taskForm.owner) {
      setTaskForm({
        noteIndex,
        processProjectId: taskForm.processProjectId || processProjects[0]?.id || "",
        owner: taskForm.owner
      });
      return;
    }

    const taskRef = await createMonthlyProcess({
      companyName: editProject?.firmaAdi || "",
      companyShortName: editProject?.firmaKisaAdi || "",
      title: noteItem.text,
      month: new Date().toISOString().slice(0, 7),
      owner: taskForm.owner,
      processProjectId: taskForm.processProjectId,
      template: {
        id: `project-note-${Date.now()}`,
        name: "Proje Notu Görevi",
        steps: [{ title: "Not görevini takip et", order: 1, groupTitle: "Proje Notu", isRequired: true }],
        autoRepeatDefault: false
      }
    });

    const nextNotes = projectNotes.map((note, index) => (
      index === noteIndex
        ? {
            ...note,
            linkedTaskId: taskRef.id,
            linkedTaskProjectId: taskForm.processProjectId,
            linkedTaskOwner: taskForm.owner,
            linkedTaskCreatedAt: new Date().toISOString(),
            linkedTaskCreatedBy: currentUserEmail || ""
          }
        : note
    ));

    setEditProject({ ...editProject, notes: nextNotes });
    await updateProject({ ...editProject, notes: nextNotes }, { silent: true });

    alert("Not görev kartı olarak oluşturuldu.");
    setTaskForm({ noteIndex: null, processProjectId: "", owner: "" });
  };

  const deleteProjectNote = async (noteItem, noteIndex) => {
    if (!isAdmin) return;

    const confirmDeleteNote = window.appConfirm
      ? await window.appConfirm(
          "Bu proje notu silinsin mi?",
          { title: "Not silinsin mi?", type: "danger", confirmText: "Notu Sil" }
        )
      : window.confirm("Bu proje notu silinsin mi?");

    if (!confirmDeleteNote) return;

    if (noteItem.linkedTaskId) {
      const deleteLinkedTask = window.appConfirm
        ? await window.appConfirm(
            "Bu nota bağlı oluşturulmuş bir görev kartı var. Not silinirken bağlı görev de silinsin mi?",
            {
              title: "Bağlı görev var",
              type: "warning",
              confirmText: "Not + Görevi Sil",
              cancelText: "Sadece Notu Sil"
            }
          )
        : window.confirm("Bu nota bağlı görev kartı var. Görev de silinsin mi?");

      if (deleteLinkedTask) {
        await deleteMonthlyProcess(noteItem.linkedTaskId);
      }
    }

    const nextNotes = projectNotes.filter((_, index) => index !== noteIndex);

    setEditProject({ ...editProject, notes: nextNotes });
    await updateProject({ ...editProject, notes: nextNotes }, { silent: true });
  };

  const filteredFiles = (editProject?.files || []).filter((file) => {
  const search = fileSearchText.toLowerCase().trim();

  const matchesSearch =
    !search ||
    file.name?.toLowerCase().includes(search) ||
    file.category?.toLowerCase().includes(search) ||
    file.uploadedBy?.toLowerCase().includes(search);

  const matchesCategory =
    fileFilterCategory === "all" ||
    file.category === fileFilterCategory;

  return matchesSearch && matchesCategory;
  });

  const sortedFiles = [...filteredFiles].sort((a, b) => {
    return new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0);
  });

  const groupedFiles = sortedFiles.reduce((acc, file) => {
  const category = file.category || "Genel";

  if (!acc[category]) {
    acc[category] = [];
  }

  acc[category].push(file);

  return acc;
  }, {});

    const formatFileSize = (bytes) => {
      if (!bytes) return "-";

      const mb = bytes / (1024 * 1024);

      if (mb >= 1) {
        return `${mb.toFixed(2)} MB`;
      }

      const kb = bytes / 1024;
      return `${kb.toFixed(0)} KB`;
      };

    const getFileIcon = (fileName = "") => {
      const extension = fileName.split(".").pop()?.toLowerCase();

      if (extension === "pdf") return "📕";
      if (["xls", "xlsx"].includes(extension)) return "📊";
      if (["doc", "docx"].includes(extension)) return "📄";
      if (["jpg", "jpeg", "png", "webp"].includes(extension)) return "🖼️";

      return "📎";
    };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 24
      }}
    >
      <div
        className="force-light-form"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 24,
          boxShadow: "0 30px 80px rgba(0,0,0,0.25)",
          width: "92%",
          maxWidth: 1050,
          maxHeight: "88vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #eee", padding: "28px 36px 18px" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 30 }}>Proje Detayı</h2>
            <p style={{ marginTop: 8, color: "#666" }}>
              {canEdit ? "Proje bilgilerini düzenleyebilirsiniz" : "Proje bilgilerini görüntüleyebilirsiniz"}
            </p>
          </div>

          <button
            onClick={() => {
              setSelectedProject(null);
              setEditProject(null);
            }}
            style={{ border: "none", background: "transparent", fontSize: 28, cursor: "pointer" }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "24px 36px", overflowY: "auto", flex: 1 }}>
        <p>
          <strong>Teknopark Şubesi:</strong> {selectedBranch?.name || editProject?.teknoparkAdi || "-"}
          {selectedBranch?.technoparkName && <span style={{ color: "#64748b" }}> — {selectedBranch.technoparkName}</span>}
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={{ gridColumn: "1 / 3" }}>
            <label style={{ fontWeight: 600 }}>Firma</label>
            <input disabled={!canEdit} style={detailInputStyle} value={editProject?.firmaAdi || ""} onChange={(e) => setEditProject({ ...editProject, firmaAdi: e.target.value })} />
          </div>

          <div style={{ gridColumn: "1 / 3" }}>
            <label style={{ fontWeight: 600 }}>Firma Kısa Adı</label>
            <input
              disabled={!canEdit}
              style={detailInputStyle}
              value={editProject?.firmaKisaAdi || ""}
              onChange={(e) => setEditProject({ ...editProject, firmaKisaAdi: e.target.value })}
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Teknopark Şubesi</label>
            <select
              disabled={!canEdit || branchOptions.length === 0}
              style={detailInputStyle}
              value={selectedBranch?.id || editProject?.branchId || ""}
              onChange={(e) => changeProjectBranch(e.target.value)}
            >
              <option value="" disabled>{branchOptions.length ? "Şube seçin" : "Firma şubesi bulunamadı"}</option>
              {branchOptions.map((branch) => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Teknopark</label>
            <input disabled style={detailInputStyle} value={selectedBranch?.technoparkName || editProject?.teknoparkAdi || ""} />
          </div>

          <div style={{ gridColumn: "1 / 3" }}>
            <label style={{ fontWeight: 600 }}>Proje Adı</label>
            <input disabled={!canEdit} style={detailInputStyle} value={editProject?.projeAdi || ""} onChange={(e) => setEditProject({ ...editProject, projeAdi: e.target.value })} />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>STB Kodu</label>
            <input disabled={!canEdit} style={detailInputStyle} value={editProject?.projeStbKodu || ""} onChange={(e) => setEditProject({ ...editProject, projeStbKodu: e.target.value })} />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Proje Kategorisi</label>
            <select disabled={!canEdit} style={detailInputStyle} value={editProject?.projeKategorisi || ""} onChange={(e) => setEditProject({ ...editProject, projeKategorisi: e.target.value })}>
              <option value="">Seçiniz</option>
              <option value="Yazılım">Yazılım</option>
              <option value="Ar-Ge">Ar-Ge</option>
              <option value="Tasarım">Tasarım</option>
            </select>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18, marginTop: 24 }}>
          {[
            ["Başlangıç Tarihi", "baslangicTarihi"],
            ["Bitiş Tarihi", "bitisTarihi"],
            ["Ek Süre Tarihi", "ekSureTarihi"],
            ["Sonlandırma Tarihi", "sonlandirmaTarihi"]
          ].map(([label, field]) => (
            <div key={field}>
              <label style={{ fontWeight: 600 }}>{label}</label>
              <input disabled={!canEdit} type="date" style={detailInputStyle} value={dateInputValue(editProject?.[field])} onChange={(e) => setEditProject({ ...editProject, [field]: e.target.value })} />
            </div>
          ))}
        </div>

        <div style={{ background: "#f8fafc", padding: 24, borderRadius: 18, marginTop: 28 }}>
          <h3 style={{ marginTop: 0 }}>Personel Bilgileri</h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}>
            {[
              ["Ar-Ge Personeli", "argePersoneli"],
              ["Yazılım Personeli", "yazilimPersoneli"],
              ["Destek Personeli", "destekPersoneli"]
            ].map(([label, field]) => (
              <div key={field}>
                <label style={{ fontWeight: 600 }}>{label}</label>
                <input disabled={!canEdit} type="number" style={detailInputStyle} value={editProject?.[field] || 0} onChange={(e) => setEditProject({ ...editProject, [field]: e.target.value })} />
              </div>
            ))}

            <div
              style={{
                background: isDark ? "#162235" : "#eef4ff",
                border: `1px solid ${isDark ? "#334155" : "#bfdbfe"}`,
                borderRadius: 14,
                padding: 16
              }}
            >
              <strong style={{ color: isDark ? "#cbd5e1" : "inherit" }}>Toplam Personel</strong>
              <div style={{ fontSize: 24, fontWeight: 800, color: isDark ? "#60a5fa" : "#2563eb" }}>
                {Number(editProject?.argePersoneli || 0) + Number(editProject?.yazilimPersoneli || 0) + Number(editProject?.destekPersoneli || 0)}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            background: isDark ? "#2a1d12" : "#fff7d6",
            border: `1px solid ${isDark ? "#7c4a18" : "#fde68a"}`,
            padding: 16,
            borderRadius: 14,
            marginTop: 24
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8, color: isDark ? "#fed7aa" : "#854d0e" }}>
            Kalan Ay Süre: {calculateKalanAySureForProject(editProject)}
          </div>
          <div style={{ color: isDark ? "#fde68a" : "#555", fontWeight: 600 }}>
            Proje Süresi: {calculateProjeSuresiForProject(editProject)}
          </div>
        </div>

        <div style={{ background: "#f8fafc", padding: 20, borderRadius: 16, marginTop: 24 }}>
          <h3 style={{ marginTop: 0 }}>Proje Notları</h3>

          {canEdit && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, marginBottom: 14 }}>
              <textarea
                value={projectNoteText}
                onChange={(event) => setProjectNoteText(event.target.value)}
                placeholder="Proje ile ilgili not yazın..."
                rows={2}
                style={{ ...detailInputStyle, margin: 0, resize: "vertical" }}
              />
              <button
                onClick={addProjectNote}
                style={{ padding: "10px 16px", border: "none", borderRadius: 10, background: "#124254", color: "#fff", fontWeight: 800, cursor: "pointer" }}
              >
                Not Ekle
              </button>
            </div>
          )}

          {projectNotes.length === 0 ? (
            <div style={{ color: "#64748b", fontWeight: 700 }}>Henüz proje notu yok.</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {projectNotes.map((noteItem, noteIndex) => (
                <div key={`${noteItem.createdAt || noteIndex}`} style={{ border: "1px solid #e2e8f0", background: "#fff", borderRadius: 12, padding: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ fontWeight: 700, color: "#334155" }}>{noteItem.text}</div>

                    {noteItem.linkedTaskId && (
                      <span style={{ padding: "4px 8px", borderRadius: 999, background: "#dcfce7", color: "#166534", fontSize: 11, fontWeight: 900, whiteSpace: "nowrap" }}>
                        Görev oluşturuldu
                      </span>
                    )}
                  </div>

                  <div style={{ color: "#94a3b8", fontSize: 12, fontWeight: 700, marginTop: 6 }}>
                    {noteItem.createdAt ? new Date(noteItem.createdAt).toLocaleString("tr-TR") : ""} {noteItem.createdBy ? `• ${noteItem.createdBy}` : ""}
                  </div>

                  {canEdit && (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
                      {taskForm.noteIndex === noteIndex && (
                        <>
                          <select
                            value={taskForm.processProjectId}
                            onChange={(event) => setTaskForm({ ...taskForm, processProjectId: event.target.value, owner: "" })}
                            style={{ padding: "9px 10px", borderRadius: 9, border: "1px solid #cbd5e1" }}
                          >
                            <option value="">Süreç projesi seç</option>
                            {processProjects.map((item) => (
                              <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                          </select>

                          <select
                            value={taskForm.owner}
                            onChange={(event) => setTaskForm({ ...taskForm, owner: event.target.value })}
                            style={{ padding: "9px 10px", borderRadius: 9, border: "1px solid #cbd5e1" }}
                          >
                            <option value="">Sorumlu seç</option>
                            {processOwners.map((owner) => (
                              <option key={owner.id} value={owner.name}>{owner.name}</option>
                            ))}
                          </select>
                        </>
                      )}

                      {!noteItem.linkedTaskId && (
                        <button
                          onClick={() => createTaskFromNote(noteItem, noteIndex)}
                          style={{ padding: "9px 12px", border: "none", borderRadius: 9, background: "#0f766e", color: "#fff", fontWeight: 800, cursor: "pointer" }}
                        >
                          Görev Olarak Oluştur
                        </button>
                      )}

                      {isAdmin && (
                        <button
                          onClick={() => deleteProjectNote(noteItem, noteIndex)}
                          style={{ padding: "9px 12px", border: "none", borderRadius: 9, background: "#ef4444", color: "#fff", fontWeight: 800, cursor: "pointer" }}
                        >
                          Notu Sil
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 24 }}>
          <div>
            <label style={{ fontWeight: 600 }}>Proje Bitirme Belgesi</label>
            <select disabled={!canEdit} style={detailInputStyle} value={editProject?.projeBitirmeBelgesi || "Yok"} onChange={(e) => setEditProject({ ...editProject, projeBitirmeBelgesi: e.target.value })}>
              <option value="Var">Var</option>
              <option value="Yok">Yok</option>
            </select>
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Yazılım Tescil Belgesi</label>
            <select disabled={!canEdit} style={detailInputStyle} value={editProject?.yazilimTescilBelgesi || "Yok"} onChange={(e) => setEditProject({ ...editProject, yazilimTescilBelgesi: e.target.value })}>
              <option value="Var">Var</option>
              <option value="Yok">Yok</option>
            </select>
          </div>
        </div>

        {editProject?.projeBitirmeBelgesi?.trim() === "Var" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 20 }}>
            <div>
              <label style={{ fontWeight: 600 }}>Bitirme Belgesi Kodu</label>
              <input disabled={!canEdit} style={detailInputStyle} value={editProject?.projeBitirmeBelgesiKodu || ""} onChange={(e) => setEditProject({ ...editProject, projeBitirmeBelgesiKodu: e.target.value })} />
            </div>

            <div>
              <label style={{ fontWeight: 600 }}>Bitirme Belgesi Tarihi</label>
              <input disabled={!canEdit} type="date" style={detailInputStyle} value={dateInputValue(editProject?.projeBitirmeBelgesiTarihi)} onChange={(e) => setEditProject({ ...editProject, projeBitirmeBelgesiTarihi: e.target.value })} />
            </div>
          </div>
        )}

        {editProject?.yazilimTescilBelgesi?.trim() === "Var" && (
          <div style={{ marginTop: 20 }}>
            <label style={{ fontWeight: 600 }}>Yazılım Tescil Tarihi</label>
            <input disabled={!canEdit} type="date" style={detailInputStyle} value={dateInputValue(editProject?.yazilimTescilTarihi)} onChange={(e) => setEditProject({ ...editProject, yazilimTescilTarihi: e.target.value })} />
          </div>
        )}

        <div style={{ marginTop: 24 }}>
          <label style={{ fontWeight: 700, fontSize: 16 }}>Proje Dosyaları</label>

          {canEdit && (
            <div style={{ marginTop: 12, marginBottom: 18, padding: 16, borderRadius: 14, background: "#f8fafc", border: "1px solid #e5e7eb" }}>
              <select value={fileCategory} onChange={(e) => setFileCategory(e.target.value)} style={{ width: "100%", maxWidth: 360, padding: "11px 12px", borderRadius: 10, border: "1px solid #dbe2ea", marginBottom: 12 }}>
                <option value="Genel">Genel</option>
                <option value="Faaliyet Raporu">Faaliyet Raporu</option>
                <option value="YMM">YMM</option>
                <option value="Proje Belgesi">Proje Belgesi</option>
                <option value="Proje Bitirme Belgesi">Proje Bitirme Belgesi</option>
                <option value="Yazılım Tescil">Yazılım Tescil</option>
                <option value="Sözleşme">Sözleşme</option>
                <option value="Fatura">Fatura</option>
                <option value="Sunum">Sunum</option>
                <option value="Diğer">Diğer</option>
              </select>

              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDraggingFile(true);
                }}
                onDragLeave={() => {
                  setIsDraggingFile(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingFile(false);

                  if (isUploadingFile) return;

                  uploadProjectFile({
                    target: {
                      files: e.dataTransfer.files,
                      value: ""
                    }
                  });
                }}
                style={{
                  marginTop: 12,
                  padding: "28px 20px",
                  borderRadius: 18,
                  border: isDraggingFile ? "2px dashed #2563eb" : "2px dashed #cbd5e1",
                  background: isDraggingFile ? "#eff6ff" : "#f8fafc",
                  textAlign: "center",
                  transition: "0.2s",
                  cursor: isUploadingFile ? "not-allowed" : "pointer",
                  opacity: isUploadingFile ? 0.7 : 1
                }}
              >
                <div style={{ fontSize: 16, fontWeight: 700, color: "#124254", marginBottom: 8 }}>
                  Dosyaları buraya sürükleyin
                </div>

                <div style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}>
                  veya bilgisayardan seçin
                </div>

                <input
                  type="file"
                  multiple
                  disabled={isUploadingFile}
                  onChange={uploadProjectFile}
                />

                {isUploadingFile && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 14,
                      borderRadius: 14,
                      background: "#f3f6fb",
                      border: "1px solid #dbeafe"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontWeight: 700, color: "#124254" }}>
                      <span>Dosyalar yükleniyor...</span>
                      <span>%{uploadProgress}</span>
                    </div>

                    <div style={{ width: "100%", height: 10, background: "#dbeafe", borderRadius: 999, overflow: "hidden" }}>
                      <div style={{ width: `${uploadProgress}%`, height: "100%", background: "#2563eb", transition: "0.3s" }} />
                    </div>

                    <div style={{ marginTop: 10, fontSize: 13, color: "#475569" }}>
                      {uploadedCount} / {uploadTotalCount} dosya tamamlandı
                    </div>

                    <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: "#111827" }}>
                      {uploadingFileName}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 240px",
              gap: 12,
              marginBottom: 16
            }}
          >
            <input
              placeholder="Dosya adı, kategori veya yükleyen ara..."
              value={fileSearchText}
              onChange={(e) => setFileSearchText(e.target.value)}
              style={{
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid #dbe2ea"
              }}
            />

            <select
              value={fileFilterCategory}
              onChange={(e) => setFileFilterCategory(e.target.value)}
              style={{
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid #dbe2ea"
              }}
            >
              <option value="all">Tüm Kategoriler</option>
              <option value="Genel">Genel</option>
              <option value="Faaliyet Raporu">Faaliyet Raporu</option>
              <option value="YMM">YMM</option>
              <option value="Proje Belgesi">Proje Belgesi</option>
              <option value="Proje Bitirme Belgesi">Proje Bitirme Belgesi</option>
              <option value="Yazılım Tescil">Yazılım Tescil</option>
              <option value="Sözleşme">Sözleşme</option>
              <option value="Fatura">Fatura</option>
              <option value="Sunum">Sunum</option>
              <option value="Diğer">Diğer</option>
            </select>
          </div>

          <div style={{ display: "grid", gap: 18 }}>
              {filteredFiles.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 12,
                    background: "#f9fafb",
                    border: "1px dashed #d1d5db",
                    color: "#6b7280"
                  }}
                >
                  Henüz dosya yüklenmemiş.
                </div>
              ) : (
                Object.entries(groupedFiles).map(([category, files]) => (
                  <div key={category}>
                    <div
                      onClick={() =>
                        setCollapsedCategories({
                          ...collapsedCategories,
                          [category]: !collapsedCategories[category]
                        })
                      }

                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 10px 24px rgba(0,0,0,0.10)";
                      }}

                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 18px rgba(0,0,0,0.06)";
                      }}

                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        marginBottom: 14,
                        padding: "14px 18px",
                        borderRadius: 16,
                        cursor: "pointer",
                        userSelect: "none",
                        transition: "0.2s",
                        boxShadow: "0 6px 18px rgba(0,0,0,0.06)",

                        background:
                          category === "YMM"
                            ? "linear-gradient(135deg,#fee2e2,#fecaca)"
                            : category === "Faaliyet Raporu"
                            ? "linear-gradient(135deg,#dcfce7,#bbf7d0)"
                            : category === "Proje Belgesi"
                            ? "linear-gradient(135deg,#dbeafe,#bfdbfe)"
                            : "linear-gradient(135deg,#eef2ff,#e0e7ff)",

                        border: "1px solid rgba(255,255,255,0.5)"
                      }}
                    >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
                  }}
                >
                  {category === "YMM"
                    ? "📕"
                    : category === "Faaliyet Raporu"
                    ? "📗"
                    : category === "Proje Belgesi"
                    ? "📘"
                    : category === "Sözleşme"
                    ? "📝"
                    : category === "Fatura"
                    ? "💳"
                    : category === "Sunum"
                    ? "📊"
                    : "📁"}
                </div>

                <div>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 15,
                      color: "#111827"
                    }}
                  >
                    {category}
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      color: "#4b5563",
                      marginTop: 2
                    }}
                  >
                    <span style={{ marginRight: 8 }}>
                      {collapsedCategories[category] ? "▶" : "▼"}
                    </span>
                    Bu kategoride {files.length} dosya bulunuyor
                  </div>
                </div>
              </div>

              <div
                style={{
                  minWidth: 34,
                  height: 34,
                  padding: "0 12px",
                  borderRadius: 999,
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  color: "#111827",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.06)"
                }}
              >
                {files.length}
              </div>
            </div>

        {!collapsedCategories[category] && (
          <div style={{ display: "grid", gap: 10 }}>
            {files.map((file, index) => (
            <div
              key={index}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 16,
                alignItems: "center",
                padding: "18px 20px",
                border: "1px solid #e5e7eb",
                borderRadius: 18,
                background: "#fff",
                boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
                transition: "all 0.22s ease",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 14px 34px rgba(0,0,0,0.10)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(0,0,0,0.05)";
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                    borderLeft:
                    file.category === "YMM"
                      ? "6px solid #dc2626"
                      : file.category === "Faaliyet Raporu"
                      ? "6px solid #16a34a"
                      : file.category === "Proje Belgesi"
                      ? "6px solid #2563eb"
                      : "6px solid #6366f1",
                  }}
                >
                  <span style={{ fontSize: 24 }}>
                    {getFileIcon(file.name)}
                  </span>

                  <strong
                    style={{
                      fontSize: 14,
                      color: "#111827",
                      wordBreak: "break-word"
                    }}
                  >
                    {file.name || "Dosya"}
                  </strong>
                </div>

                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: "#6b7280"
                  }}
                >
                  📦 {formatFileSize(file.size)} •{" "}
                  {file.uploadedAt
                    ? new Date(file.uploadedAt).toLocaleString("tr-TR")
                    : "-"}
                </div>

                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: "#9ca3af"
                  }}
                >
                  👤 {file.uploadedBy || "Bilinmeyen Kullanıcı"}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center"
                }}
              >
                <button
                  onClick={() => setPreviewFile(file)}
                  style={{
                    padding: "9px 13px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    color: "#111827",
                    fontWeight: 700,
                    cursor: "pointer"
                  }}
                >
                  Görüntüle
                </button>

                <a
                  href={file.url}
                  target="_blank"
                  rel="noreferrer"
                  download
                  style={{
                    padding: "9px 13px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    background: "#f9fafb",
                    color: "#111827",
                    fontWeight: 700,
                    textDecoration: "none"
                  }}
                >
                  İndir
                </a>

                {canEdit && (
                  <button
                    onClick={() => deleteProjectFile(index)}
                    style={{
                      padding: "9px 13px",
                      borderRadius: 10,
                      border: "none",
                      background: "#ef4444",
                      color: "#fff",
                      fontWeight: 700,
                      cursor: "pointer"
                    }}
                  >
                    Sil
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    ))
  )}
</div>
        </div>
        </div>

        {editProject?.archiveApprovalStatus === "pending" && (
          <div
            style={{
              margin: "0 36px 18px",
              padding: "14px 16px",
              borderRadius: 12,
              border: "1px solid #fbbf24",
              background: "#fffbeb",
              color: "#92400e"
            }}
          >
            <strong>Admin onayı bekleyen arşivleme talebi</strong>
            <div style={{ marginTop: 6, lineHeight: 1.5 }}>
              {editProject.archiveReason || "Gerekçe belirtilmedi."}
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: "#a16207" }}>
              Talep eden: {editProject.archiveRequestedBy || "-"}
            </div>
          </div>
        )}

        {editProject?.archiveApprovalStatus === "rejected" && (
          <div
            style={{
              margin: "0 36px 18px",
              padding: "14px 16px",
              borderRadius: 12,
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#991b1b"
            }}
          >
            <strong>Son arşivleme talebi reddedildi</strong>
            <div style={{ marginTop: 6, lineHeight: 1.5 }}>
              {editProject.archiveRejectionReason || "Red nedeni belirtilmedi."}
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: "#b91c1c" }}>
              Reddeden: {editProject.archiveRejectedBy || "-"}. Yeni bir gerekçeyle tekrar talep oluşturabilirsiniz.
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            padding: "18px 36px",
            borderTop: "1px solid #e5e7eb",
            background: "#fff",
            boxShadow: "0 -10px 24px rgba(15,23,42,0.06)"
          }}
        >
          <button
            onClick={() => {
              setSelectedProject(null);
              setEditProject(null);
            }}
            style={{
              padding: "12px 22px",
              borderRadius: 10,
              border: "1px solid #ccc",
              background: "#fff",
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            Kapat
          </button>

          {canEdit && (
            <>
              <button
                onClick={deleteProject}
                disabled={isUploadingFile || editProject?.archiveApprovalStatus === "pending"}
                style={{
                  padding: "12px 22px",
                  borderRadius: 10,
                  border: "none",
                  background: editProject?.archiveApprovalStatus === "pending" ? "#94a3b8" : "#f59e0b",
                  color: "#fff",
                  fontWeight: 700,
                  opacity: isUploadingFile ? 0.6 : 1,
                  cursor: isUploadingFile || editProject?.archiveApprovalStatus === "pending" ? "not-allowed" : "pointer"
                }}
              >
                {editProject?.archiveApprovalStatus === "pending"
                  ? "Admin Onayı Bekleniyor"
                  : "Arşivleme Talebi"}
              </button>

              <button
                onClick={() => updateProject()}
                disabled={isUploadingFile}
                style={{
                  padding: "12px 22px",
                  borderRadius: 10,
                  border: "none",
                  background: "#2563eb",
                  color: "#fff",
                  fontWeight: 700,
                  opacity: isUploadingFile ? 0.6 : 1,
                  cursor: isUploadingFile ? "not-allowed" : "pointer"
                }}
              >
                Güncelle
              </button>
            </>
          )}
                </div>
      </div>
    </div>
  );
}
